import { createAction, createSlice } from "@reduxjs/toolkit";
import { handleFail, handleLoading } from "redux/redux-util";
import concat from "lodash/concat";
import compact from "lodash/compact";
import unionBy from "lodash/unionBy";
import { mergeObj } from "utils/common-util";

const initialState = {
  loading: false,
  userInfo: undefined,
  isUhnwUser: false,
  isPwmUser: false,
  registered: null,
  suitability: null,
  riskResult: {},
  listAssetAllocation: [],
  accounts: [],
  accountsFCY: [],
  globalAccounts: [],
  additionalAccounts: [],
  uhmf: {
    loading: false,
    list: [],
  },
  fetchUserTypeOnce: false,
  bankruptcyPassed: false,
  muleAccount: false,
};

const slice = createSlice({
  initialState,
  name: "customer",
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    getProfile: () => {},
    getProfileSuccess: (state, action) => {
      state.userInfo = action.payload;
    },
    getProfileFailure: handleFail,

    getPrimaryLombardAccounts: (state) => {
      state.loading = true;
      state.accounts = [];
    },
    getAccounts: handleLoading,
    getAccountsSuccess: (state, action) => {
      state.loading = false;
      state.accounts = action.payload;
    },
    getAccountsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    getAccountsFCY: handleLoading,
    getAccountsFCYSuccess: (state, action) => {
      state.loading = false;
      state.accountsFCY = action.payload;
    },
    getAccountsFCYFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    getSecondaryLombardAccounts: (state) => {
      state.loading = true;
      state.globalAccounts = [];
    },
    getGlobalAccounts: handleLoading,
    getGlobalAccountsSuccess: (state, action) => {
      state.loading = false;
      state.globalAccounts = action.payload;
      state.error = null;
    },
    getGlobalAccountsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    addAdditionalAccount: (state, action) => {
      const { accounts, account } = action.payload;
      state.additionalAccounts = unionBy(state.additionalAccounts, compact(concat(accounts, account)), "accountNo");
    },
    clearAdditionalAccount: (state) => {
      if (state.additionalAccounts.length !== 0) state.additionalAccounts = [];
    },

    clearCustomerAccounts: (state) => {
      state.accounts = [];
      state.accountsFCY = [];
      state.globalAccounts = [];
      state.additionalAccounts = [];
    },

    markUserIsUHNW: (state) => {
      state.isUhnwUser = true;
    },

    markUserIsPWM: (state) => {
      state.isPwmUser = true;
    },

    getUhmfList: (state) => {
      state.uhmf.loading = true;
    },
    cancelFetchUhmfList: (state) => {
      state.uhmf.loading = false;
    },
    getUhmfListSuccess: (state, action) => {
      state.uhmf.loading = false;
      state.uhmf.list = action.payload;
      state.uhmf.done = true;
    },
    getUhmfListFailure: (state) => {
      state.uhmf.loading = false;
      state.uhmf.done = true;
    },
    setRegisteredStatus: (state, action) => {
      state.registered = action.payload;
    },
    setSuitabilityResult: (state, action) => {
      state.suitability = action.payload;
    },
    getSuitabilityData: (state) => {
      state.riskResultPending = true;
    },
    getSuitabilityDataSuccess: (state, action) => {
      state.riskResultPending = false;
      state.riskResult = mergeObj(state.riskResult, action.payload);
      state.listAssetAllocation = getListAssetAllocation(state.riskResult);
    },
    getSuitabilityDataFailure: (state) => {
      state.riskResultPending = false;
      state.riskResult = {};
    },
    checkRegistered: () => {},
    checkRegisteredSuccess: (state, action) => {
      state.registered = action.payload;
    },
    checkRegisteredFailure: (state) => {
      state.registered = false;
    },
    checkUserType: (state) => {
      state.checkingUserType = true;
    },
    checkUserTypeSuccess: (state, action) => {
      const { uhnw, gmCustomer, wealthFlag, specialFlag, ...pwmRegisteringStatus } = action.payload;
      state.isUhnwUser = uhnw;
      state.wealthFlag = wealthFlag;
      state.specialFlag = specialFlag;
      state.isPB = !!wealthFlag;
      state.isPPB = !!specialFlag;
      state.isPwmUser = gmCustomer || uhnw;
      state.isFinalPwmUser = gmCustomer;
      state.pwmRegisteringStatus = pwmRegisteringStatus;
      state.checkingUserType = false;
      if (!state.fetchUserTypeOnce) {
        state.fetchUserTypeOnce = true;
      }
    },
    checkUserTypeFailure: (state) => {
      state.isUhnwUser = false;
      state.isPwmUser = false;
      state.pwmRegisteringStatus = {};
      state.checkingUserType = false;
    },
    passBankruptcyValidate: (state) => {
      state.bankruptcyPassed = true;
    },
    maskAsMuleAccount: (state) => {
      state.muleAccount = true;
    },
  },
});

export const checkSuitability = createAction(`${slice.name}/checkSuitability`);
export const {
  clearError,
  getProfile,
  getProfileSuccess,
  getProfileFailure,
  getUhmfList,
  getUhmfListSuccess,
  getUhmfListFailure,
  cancelFetchUhmfList,
  getPrimaryLombardAccounts,
  getAccounts,
  getAccountsSuccess,
  getAccountsFailure,
  getAccountsFCY,
  getAccountsFCYSuccess,
  getAccountsFCYFailure,
  clearCustomerAccounts,
  getSecondaryLombardAccounts,
  getGlobalAccounts,
  getGlobalAccountsSuccess,
  getGlobalAccountsFailure,
  markUserIsUHNW,
  addAdditionalAccount,
  clearAdditionalAccount,
  setRegisteredStatus,
  setSuitabilityResult,
  getSuitabilityData,
  getSuitabilityDataSuccess,
  getSuitabilityDataFailure,
  markUserIsPWM,
  checkRegistered,
  checkRegisteredSuccess,
  checkRegisteredFailure,
  checkUserType,
  checkUserTypeSuccess,
  checkUserTypeFailure,
  passBankruptcyValidate,
  maskAsMuleAccount,
} = slice.actions;

export default slice.reducer;

function getListAssetAllocation(riskResult) {
  let listAssetAllocation = [];
  if (riskResult.mergeColumn) {
    switch (riskResult.mergeColumn) {
      case "1/2":
        listAssetAllocation = [
          {
            code: "val1",
            label: "riskresult.content3.savingShortFundAndlongBond",
            display: riskResult.displaySavingShortFund,
            percentData: riskResult.valSavingShortFund,
          },
          {
            code: "val2",
            label: "riskresult.content3.corpBond",
            display: riskResult.displayCorpBond,
            percentData: riskResult.valCorpBond,
          },
          {
            code: "val3",
            label: "riskresult.content3.equity",
            display: riskResult.displayEquity,
            percentData: riskResult.valEquity,
          },
          {
            code: "val4",
            label: "riskresult.content3.alterInvs",
            display: riskResult.displayAlterInvs,
            percentData: riskResult.valAlterInvs,
          },
        ];
        break;
      case "2/3":
        listAssetAllocation = [
          {
            code: "val1",
            label: "riskresult.content3.savingShortFund",
            display: riskResult.displaySavingShortFund,
            percentData: riskResult.valSavingShortFund,
          },
          {
            code: "val2",
            label: "riskresult.content3.longBondAndcorpBond",
            display: riskResult.displayCorpBond,
            percentData: riskResult.valCorpBond,
          },
          {
            code: "val3",
            label: "riskresult.content3.equity",
            display: riskResult.displayEquity,
            percentData: riskResult.valEquity,
          },
          {
            code: "val4",
            label: "riskresult.content3.alterInvs",
            display: riskResult.displayAlterInvs,
            percentData: riskResult.valAlterInvs,
          },
        ];
        break;
      default:
        listAssetAllocation = [
          {
            code: "val1",
            label: "riskresult.content3.savingShortFund",
            display: riskResult.displaySavingShortFund,
            percentData: riskResult.valSavingShortFund,
          },
          {
            code: "val2",
            label: "riskresult.content3.longBond",
            display: riskResult.displayLongBond,
            percentData: riskResult.valLongBond,
          },
          {
            code: "val3",
            label: "riskresult.content3.corpBond",
            display: riskResult.displayCorpBond,
            percentData: riskResult.valCorpBond,
          },
          {
            code: "val4",
            label: "riskresult.content3.equity",
            display: riskResult.displayEquity,
            percentData: riskResult.valEquity,
          },
          {
            code: "val5",
            label: "riskresult.content3.alterInvs",
            display: riskResult.displayAlterInvs,
            percentData: riskResult.valAlterInvs,
          },
        ];
    }
  }

  return listAssetAllocation;
}
